import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import FontBody3 from "../../../../../../components/fonts/font-body3/font-body3";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import IconDelete from "../../../../../../components/icons/icon-delete/icon-delete";
import IconEdit from "../../../../../../components/icons/icon-edit/icon-edit";
import IconTranslationIndicator
	from "../../../../../../components/icons/icon-translation-indicator/icon-translation-indicator";
import Icon from "../../../../../../components/icons/icon/icon";
import { ButtonIcon } from "../../../../../../components/inputs/button-icon/button-icon";
import GridLine from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineButtons
	from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import GridLineIndicators
	from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-indicators/grid-line-indicators";
import GridLineItem
	from "../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import { Tooltip, TOOLTIP_VARIANTS } from "../../../../../../components/tooltip/tooltip";
import { RESOLUTION_CATEGORY } from "../../../../../../models/ResolutionTypes";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import Translation from "../../../../../../translation/translation";
import ResolutionStore from "../../../../stores/resolution-store";
import ResolutionLineItemRange from "./resolution-line-item-range/resolution-line-item-range";
import ResolutionLineItemType from "./resolution-line-item-type/resolution-line-item-type";
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";
import { RESOLUTION_TYPE, RESOLUTION_ID } from "../../../../constants";
import { getResolutionDisplayFieldVisibility } from "../../../../utils/resolution-display-translation";

export interface ResolutionLineProps {
    category: RESOLUTION_CATEGORY;
    resolution: ResolutionStore;
    disabled?: boolean;
    deleteCallback: (resolution: ResolutionStore) => void;
}

export interface ResolutionLineItemProps {
    category: RESOLUTION_CATEGORY;
    resolution: ResolutionStore;
    disabled?: boolean;
}

const ResolutionLine: React.FC<ResolutionLineProps> = observer(({ category, resolution, disabled, deleteCallback }) => {
    const navigate = useNavigate();
    const { hasMissingTranslations } = useTranslation();

    const onResolutionClicked = () => navigate({
        pathname: resolution.id.toString()
    });

    const canDeleteResolution = (): boolean => {
        return category === RESOLUTION_CATEGORY.standard_resolutions && resolution.modifyCreditAmount !== null;
    }

	if (
		(resolution.resolutionType === RESOLUTION_TYPE.ProductExchangeType && !dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PRODUCT_EXCHANGE).isSupported) ||
		(resolution.resolutionType === RESOLUTION_TYPE.DirectVariantExchange && !dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.VARIANT_EXCHANGE).isSupported) ||
		(resolution.resolutionType === RESOLUTION_TYPE.ManualExchangeType && !dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.MANUAL_EXCHANGE).isSupported)
	) {
		return null;
	}

    const { showDisplayName } = getResolutionDisplayFieldVisibility(resolution.resolutionId as RESOLUTION_ID);

    const columnSettings = showDisplayName
        ? [{ width: 1.5 }, { width: 1 }, { width: 1 }, { width: 1 }, { width: 30, type: 'px' as const }, { width: 60, type: 'px' as const }]
        : [{ width: 1.5 }, { width: 1 }, { width: 1 }, { width: 30, type: 'px' as const }, { width: 60, type: 'px' as const }];

    return <GridLine
        onClick={() => onResolutionClicked()}
        disabled={disabled}
        settings={{
            columns: columnSettings
        }}
        columns={[
            <GridLineItem>
                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{resolution.internalName}</FontBody1>
            </GridLineItem>,

            ...(showDisplayName ? [
                <GridLineItem key="display-name">
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UPPERCASE]}>Display Name</FontBody3>
                    <Translation Font={FontBody3} color={FONT_COLOR.PRIMARY3_50} translationKey={resolution.name} />
                </GridLineItem>,
            ] : []),

            <GridLineItem>
                <ResolutionLineItemType 
                    category={category}
                    resolution={resolution}
                />
            </GridLineItem>,

            <GridLineItem>
                <ResolutionLineItemRange 
                    category={category}
                    resolution={resolution}
                />
            </GridLineItem>,
            
            <GridLineIndicators>
                {hasMissingTranslations(undefined, resolution.getTranslationKeys()) &&
                    <Tooltip
                        content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                There are untranslated items. Please edit <br />
                                the resolution for more information.
                            </FontBody2>
                        }
                        variant={TOOLTIP_VARIANTS.ERROR}
                    >
                        <Icon Component={IconTranslationIndicator} />
                    </Tooltip>
                }
            </GridLineIndicators>,

            <GridLineButtons>
                {canDeleteResolution() &&
                    <ButtonIcon Component={IconDelete} onClick={() => deleteCallback(resolution)} />
                }
                <ButtonIcon Component={IconEdit} onClick={() => onResolutionClicked()} />
            </GridLineButtons>,
        ]}
    />
});

export default ResolutionLine;